import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, Download, Share2, Target } from 'lucide-react';
import {
  AIAnalysisLoader,
  StrategyMindmap,
  NodeDetailModal,
} from '@/components/strategy';
import type { MindmapNodeType } from '@/components/strategy';

type Stage = 'loading' | 'reveal' | 'mindmap';

export default function BuyVsBuildPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('loading');
  const [isExiting, setIsExiting] = useState(false);
  const [selectedNode, setSelectedNode] = useState<MindmapNodeType | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const handleAnalysisComplete = useCallback(() => {
    setStage('reveal');
  }, []);

  useEffect(() => {
    if (stage === 'reveal') {
      const timer = setTimeout(() => {
        setStage('mindmap');
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [stage]);

  const handleExit = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      navigate(-1);
    }, 600);
  }, [navigate]);

  const handleNodeDetailClick = useCallback((node: MindmapNodeType) => {
    setSelectedNode(node);
    setIsDetailModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedNode(null);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDetailModalOpen) {
        handleExit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleExit, isDetailModalOpen]);

  return (
    <div className="min-h-screen bg-[#063F6B] text-white">
      {/* Exit transition */}
      <AnimatePresence>
        {isExiting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 bg-[#063F6B] z-[100]"
          />
        )}
      </AnimatePresence>

      {/* Loading Stage */}
      <AnimatePresence>
        {stage === 'loading' && (
          <AIAnalysisLoader onComplete={handleAnalysisComplete} duration={3500} />
        )}
      </AnimatePresence>

      {/* Reveal Stage */}
      <AnimatePresence>
        {stage === 'reveal' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center z-40"
          >
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                className="w-20 h-20 rounded-full bg-gradient-to-br from-[#EAA059] to-[#d4893e] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#EAA059]/20"
              >
                <Target className="w-10 h-10 text-white" />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                className="text-3xl font-semibold text-white tracking-tight"
              >
                Analysis Complete
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="text-[#EAA059]/90 mt-2"
              >
                Explore your strategic options
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mindmap Stage */}
      <AnimatePresence>
        {stage === 'mindmap' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            className="min-h-screen flex flex-col"
          >
            {/* Header */}
            <header className="relative z-20 px-6 py-4 flex items-center justify-between border-b border-white/[0.06]">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleExit}
                  className="p-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-slate-400 hover:text-white hover:border-[#EAA059]/25 hover:bg-white/[0.08] transition-all duration-200"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div>
                  <h1 className="text-xl font-semibold text-white tracking-tight">
                    What Should We Do Next?
                  </h1>
                  <p className="text-sm text-slate-400">
                    Buy vs Build Decision Framework
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button className="px-4 py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-slate-300 hover:text-white hover:border-[#EAA059]/25 hover:bg-white/[0.08] transition-all duration-200 flex items-center gap-2 text-sm font-medium">
                  <Share2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Share</span>
                </button>
                <button className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#EAA059] to-[#d4893e] text-white font-medium flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md hover:shadow-[#EAA059]/15 text-sm">
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </header>

            {/* Subtitle banner */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              className="px-6 py-3 bg-[#EAA059]/[0.06] border-b border-[#EAA059]/10"
            >
              <p className="text-sm text-slate-400 text-center">
                <span className="text-[#EAA059]/90 font-medium">Paths + Tradeoffs + How to Think</span>
                {' '}— Explore strategic options without prescribing a specific solution
              </p>
            </motion.div>

            <main className="flex-1 relative">
              <StrategyMindmap onNodeDetailClick={handleNodeDetailClick} />
            </main>

            <footer className="px-6 py-3 border-t border-white/[0.06] text-center">
              <p className="text-xs text-slate-500">
                This framework helps structure your thinking. Final decisions should involve stakeholder input and detailed analysis.
              </p>
            </footer>
          </motion.div>
        )}
      </AnimatePresence>

      <NodeDetailModal
        node={selectedNode}
        isOpen={isDetailModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}
